import { fmtMoney, fmtDate, STATUS_COLORS, PAY_COLORS } from "@/lib/format";
import { Shirt, MapPin, Phone } from "lucide-react";
import LoyaltyCard from "@/components/LoyaltyCard";

function Logo({ s }) {
  return s.logo_url
    ? <img src={s.logo_url} alt="logo" className="h-14 w-14 rounded-xl object-cover" />
    : <div className="h-14 w-14 rounded-xl bg-sky-500 text-white grid place-items-center"><Shirt size={26} /></div>;
}

export function buildReceiptText(o, s, cur, link) {
  const lines = [
    `*${s.company_name || "WuraPro"}*`,
    s.address || null,
    s.whatsapp ? `WhatsApp: ${s.whatsapp}` : null,
    "------------------------------",
    `Reçu N°: ${o.order_number}`,
    `Date: ${fmtDate(o.created_at)}`,
    `Client: ${o.customer_name} (${o.customer_phone})`,
    o.agent_name ? `Agent: ${o.agent_name}` : null,
    "------------------------------",
    ...o.items.map((it) => `${it.service_name} x${it.quantity}${it.pricing_type === "per_kg" ? "kg" : ""} = ${fmtMoney(it.line_total, cur)}`),
    "------------------------------",
    o.discount_amount > 0 ? `Sous-total: ${fmtMoney(o.subtotal, cur)}` : null,
    o.discount_amount > 0 ? `Remise: -${fmtMoney(o.discount_amount, cur)}${o.discount_percent ? ` (-${o.discount_percent}%)` : ""}` : null,
    `Total: ${fmtMoney(o.total, cur)}`,
    `Payé: ${fmtMoney(o.paid, cur)}`,
    `Reste: ${fmtMoney(o.remaining, cur)}`,
    `Statut: ${o.status} | ${o.payment_status}`,
    link ? `\nVoir le reçu en ligne : ${link}` : null,
    s.receipt_footer ? `\n${s.receipt_footer}` : null,
  ].filter(Boolean);
  return lines.join("\n");
}

export default function ReceiptDocument({ o, s, cur, view, loyalty }) {
  if (view === "ticket") {
    return (
      <div data-testid="receipt-ticket" className="max-w-[340px] mx-auto bg-[#fdfbf7] border-2 border-dashed border-slate-300 shadow-md p-6 font-mono text-[13px] text-slate-800 leading-relaxed">
        <div className="text-center">
          <div className="flex justify-center mb-1"><Logo s={s} /></div>
          <p className="font-bold text-base">{s.company_name || "WuraPro"}</p>
          {s.address && <p className="text-[11px]">{s.address}</p>}
          {s.whatsapp && <p className="text-[11px]">Tel: {s.whatsapp}</p>}
        </div>
        <div className="border-t border-dashed border-slate-400 my-2" />
        <p>N°: {o.order_number}</p>
        <p>Date: {fmtDate(o.created_at)}</p>
        <p>Client: {o.customer_name}</p>
        <p>Tel: {o.customer_phone}</p>
        {o.agent_name && <p>Agent: {o.agent_name}</p>}
        <div className="border-t border-dashed border-slate-400 my-2" />
        {o.items.map((it, i) => (
          <div key={i} className="flex justify-between">
            <span>{it.service_name} x{it.quantity}{it.pricing_type === "per_kg" ? "kg" : ""}</span>
            <span>{fmtMoney(it.line_total, cur)}</span>
          </div>
        ))}
        <div className="border-t border-dashed border-slate-400 my-2" />
        {o.discount_amount > 0 && (
          <>
            <div className="flex justify-between"><span>Sous-total</span><span>{fmtMoney(o.subtotal, cur)}</span></div>
            <div className="flex justify-between"><span>Remise {o.discount_percent ? `(-${o.discount_percent}%)` : ""}</span><span>-{fmtMoney(o.discount_amount, cur)}</span></div>
          </>
        )}
        <div className="flex justify-between font-bold"><span>TOTAL</span><span>{fmtMoney(o.total, cur)}</span></div>
        <div className="flex justify-between"><span>Payé</span><span>{fmtMoney(o.paid, cur)}</span></div>
        <div className="flex justify-between"><span>Reste</span><span>{fmtMoney(o.remaining, cur)}</span></div>
        <div className="flex justify-between"><span>Statut</span><span>{o.status}</span></div>
        <div className="border-t border-dashed border-slate-400 my-2" />
        {loyalty && (
          <div className="text-center text-[12px] my-1">
            <p className="font-bold">★ Fidélité : {loyalty.stamps}/{loyalty.required}{loyalty.mode === "per_kg" ? " kg" : ""}</p>
            {loyalty.rewards_available > 0
              ? <p>Recompense dispo : {loyalty.reward_value || loyalty.reward_type} !</p>
              : <p>Plus que {loyalty.remaining} {loyalty.mode === "per_kg" ? "kg" : "passage(s)"} pour {loyalty.reward_value || loyalty.reward_type}</p>}
          </div>
        )}
        <p className="text-center text-[11px]">{s.receipt_footer || "Merci de votre confiance !"}</p>
      </div>
    );
  }
  return (
    <div data-testid="receipt-a4" className="max-w-3xl mx-auto bg-white border border-slate-200 shadow-xl rounded-2xl p-8 md:p-12">
      <div className="flex items-start justify-between border-b border-slate-100 pb-6">
        <div className="flex items-center gap-4">
          <Logo s={s} />
          <div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900">{s.company_name || "WuraPro"}</h1>
            {s.opening_hours && <p className="text-xs text-slate-400">{s.opening_hours}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="font-display text-xl font-bold text-sky-600">REÇU</p>
          <p className="font-mono text-sm text-slate-700">{o.order_number}</p>
          <p className="text-xs text-slate-400">{fmtDate(o.created_at)}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 py-6 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Client</p>
          <p className="font-semibold text-slate-800">{o.customer_name}</p>
          <p className="text-slate-500 flex items-center gap-1"><Phone size={13} /> {o.customer_phone}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{o.agent_name ? "Émis par" : "Statut"}</p>
          {o.agent_name && <p className="font-semibold text-slate-800">{o.agent_name}</p>}
          <div className="flex gap-1.5 justify-end mt-1">
            <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[o.status]}`}>{o.status}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${PAY_COLORS[o.payment_status]}`}>{o.payment_status}</span>
          </div>
        </div>
      </div>

      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <th className="py-2">Service</th><th>Détail</th><th className="text-center">Qté</th><th className="text-right">Montant</th>
        </tr></thead>
        <tbody>
          {o.items.map((it, i) => (
            <tr key={i} className="border-b border-slate-50">
              <td className="py-3 font-medium text-slate-800">{it.service_name}</td>
              <td className="text-slate-500">{it.pricing_type === "per_kg" ? `${fmtMoney(it.unit_price, cur)}/kg` : "Forfait"}</td>
              <td className="text-center">{it.quantity}{it.pricing_type === "per_kg" ? " kg" : ""}</td>
              <td className="text-right font-medium">{fmtMoney(it.line_total, cur)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-end mt-6">
        <div className="w-full max-w-xs bg-slate-50 rounded-xl p-5 space-y-2 text-sm">
          {o.discount_amount > 0 && (
            <>
              <div className="flex justify-between"><span className="text-slate-500">Sous-total</span><span className="font-medium text-slate-700">{fmtMoney(o.subtotal, cur)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Remise {o.discount_percent ? `(-${o.discount_percent}%)` : ""}</span><span className="text-rose-600 font-medium">-{fmtMoney(o.discount_amount, cur)}</span></div>
            </>
          )}
          <div className="flex justify-between"><span className="text-slate-500">Total</span><span className="font-display text-lg font-bold text-slate-900">{fmtMoney(o.total, cur)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Payé</span><span className="text-emerald-600 font-semibold">{fmtMoney(o.paid, cur)}</span></div>
          <div className="flex justify-between border-t border-slate-200 pt-2"><span className="text-slate-500">Reste à payer</span><span className="text-rose-600 font-bold">{fmtMoney(o.remaining, cur)}</span></div>
        </div>
      </div>

      {loyalty && (
        <div className="mt-6 max-w-xs">
          <LoyaltyCard loyalty={loyalty} settings={s} compact />
        </div>
      )}

      <div className="border-t border-slate-100 mt-8 pt-5 text-center text-xs text-slate-400 space-y-1">
        {s.address && <p className="flex items-center justify-center gap-1"><MapPin size={12} /> {s.address}</p>}
        <p className="flex items-center justify-center gap-3">
          {s.whatsapp && <span>WhatsApp: {s.whatsapp}</span>}
          {s.instagram && <span>IG: {s.instagram}</span>}
          {s.facebook && <span>FB: {s.facebook}</span>}
        </p>
        {s.receipt_footer && <p className="text-slate-500 font-medium pt-1">{s.receipt_footer}</p>}
      </div>
    </div>
  );
}
